import {isValidAppSlug} from 'views/home/dashboard/pinnedApps';

import {CatalogApp, isReplitApp} from './catalogApp';

export const REPLIT_CREATE_URL = 'https://replit.com/new';

export const isSafeHttpsUrl = (url?: string): url is string => {
  if (!url) {
    return false;
  }

  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

export const isSafeLaunchUrl = isSafeHttpsUrl;

const isValidHostname = (hostname: string) =>
  hostname.length <= 253 &&
  hostname.split('.').every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label));

const containsUnsafeUrlCharacter = (url: string) =>
  Array.from(url).some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== undefined && (codePoint <= 31 || codePoint === 127);
  });

const hasExplicitPort = (url: string) => {
  const authority = /^https:\/\/([^/?#]+)/i.exec(url)?.[1];
  return authority?.split('@').at(-1)?.includes(':') ?? false;
};

export const isSafeReplitLaunchUrl = (url?: string): url is string => {
  if (!url || url !== url.trim() || containsUnsafeUrlCharacter(url)) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname.endsWith('.replit.app') &&
      parsed.hostname !== 'replit.app' &&
      isValidHostname(parsed.hostname) &&
      !parsed.username &&
      !parsed.password &&
      !hasExplicitPort(url) &&
      !parsed.port &&
      !parsed.search &&
      !parsed.hash
    );
  } catch {
    return false;
  }
};

export const isSafeReplitEditUrl = (url?: string): url is string => {
  if (!url || url !== url.trim() || containsUnsafeUrlCharacter(url)) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname === 'replit.com' &&
      !parsed.username &&
      !parsed.password &&
      !hasExplicitPort(url) &&
      !parsed.port &&
      !parsed.search &&
      !parsed.hash &&
      /^\/@[A-Za-z0-9][A-Za-z0-9_.-]{0,99}\/[A-Za-z0-9][A-Za-z0-9._~-]{0,199}\/?$/.test(
        parsed.pathname
      )
    );
  } catch {
    return false;
  }
};

export const displayName = (slug?: string) =>
  slug
    ?.split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') || 'Untitled app';

export const getAppDisplayName = (app: CatalogApp) =>
  isReplitApp(app) ? app.title : displayName(app.slug);

export const getSafeAppLaunchUrl = (app: CatalogApp) => {
  if (isReplitApp(app)) {
    return isSafeReplitLaunchUrl(app.launch_url) ? app.launch_url : null;
  }
  return isSafeLaunchUrl(app.launch_url) ? app.launch_url : null;
};

/** App Platform apps with a slug and launch URL can be embedded on home. */
export const isPinnableApp = (app: CatalogApp): app is CatalogApp & {slug: string} =>
  app.platform?.['.tag'] === 'company_os' &&
  Boolean(app.slug && isValidAppSlug(app.slug)) &&
  Boolean(getSafeAppLaunchUrl(app));

export const getSafeReplitEditUrl = (app: CatalogApp) => {
  if (!isReplitApp(app) || !app.platform_details.is_owned) {
    return null;
  }
  const editUrl = app.platform_details.edit_url;
  return isSafeReplitEditUrl(editUrl) ? editUrl : null;
};

export const getAppOwnerLdap = (app: CatalogApp) => {
  const principal = app.owner?.principal;
  if (principal?.['.tag'] === 'user' && principal.username) {
    return principal.username;
  }

  return undefined;
};
